import { XApiVerb } from '../types/verb';

export enum XApiVerbName {
  // Standard verbs
  Initialized = 'initialized',
  Viewed = 'viewed',
  Exited = 'exited',
  Answered = 'answered',
  Completed = 'completed',
  Suspended = 'suspended',
  Resumed = 'resumed',

  // Custom verbs
  Cleared = 'cleared',
  Checked = 'checked',
  ShowedAssistance = 'showed-assistance',
  ShowedAnswer = 'showed-answer',
  ShowedDetailedFeedback = 'showed-detailed-feedback',
  Rated = 'rated',
  AskedNext = 'asked-next',
  AskedPrevious = 'asked-previous',
  Shared = 'shared',
  Unshared = 'unshared',
  Peeked = 'peeked',
  Unpeeked = 'unpeeked',
  ExperiencedPlayed = 'experienced-played',
  ExperiencedPaused = 'experienced-paused',
  ExperiencedStopped = 'experienced-stopped',
  Screened = 'screened',
  Drew = 'drew',
  /**
   * The teacher's course-environment management actions, reported against the environment rather than
   * against a learner's attempt: `added`/`removed` for the activity bookmark, `hidden`/`un-hidden` for
   * whether students see the activity. CET renames `un-hidden` to `un_hidden`; the other three pass
   * through (see cetVerbs).
   */
  Added = 'added',
  Removed = 'removed',
  Hidden = 'hidden',
  UnHidden = 'un-hidden',
  /**
   * A question graded at submission rather than during the attempt — the 720 evaluation (exam) flow, where
   * the learner never runs a per-question check. Carries the same result as an answer (response + success +
   * score), and MOE reads it as `answered`; kept a distinct verb so the submission-time report cannot be
   * confused with a live check, and so CET (which has no mapping for it) stays out of it.
   */
  Evaluated = 'evaluated'
}

//* -------------------- Standard verbs -------------------- */

export const initialized: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/initialized',
  display: { en: XApiVerbName.Initialized }
};

export const viewed: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/viewed',
  display: { en: XApiVerbName.Viewed }
};

export const exited: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/exited',
  display: { en: XApiVerbName.Exited }
};

export const answered: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/answered',
  display: { en: XApiVerbName.Answered }
};

export const completed: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/completed',
  display: { en: XApiVerbName.Completed }
};

export const suspended: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/suspended',
  display: { en: XApiVerbName.Suspended }
};

export const resumed: XApiVerb = {
  id: 'http://id.tincanapi.com/verb/resumed',
  display: { en: XApiVerbName.Resumed }
};

/* -------------------- Custom / extended verbs -------------------- */

const customBase = 'https://learning.cet.ac.il/xapi/verbs/';

export const cleared: XApiVerb = {
  id: `${customBase}cleared`,
  display: { en: XApiVerbName.Cleared }
};

export const checked: XApiVerb = {
  id: `${customBase}checked`,
  display: { en: XApiVerbName.Checked }
};

export const showedAssistance: XApiVerb = {
  id: `${customBase}showed-assistance`,
  display: { en: XApiVerbName.ShowedAssistance }
};

export const showedAnswer: XApiVerb = {
  id: `${customBase}showed-answer`,
  display: { en: XApiVerbName.ShowedAnswer }
};

export const showedDetailedFeedback: XApiVerb = {
  id: `${customBase}showed-detailed-feedback`,
  display: { en: XApiVerbName.ShowedDetailedFeedback }
};

export const rated: XApiVerb = {
  id: `${customBase}rated`,
  display: { en: XApiVerbName.Rated }
};

export const shared: XApiVerb = {
  id: `${customBase}shared`,
  display: { en: XApiVerbName.Shared }
};

export const unshared: XApiVerb = {
  id: `${customBase}unshared`,
  display: { en: XApiVerbName.Unshared }
};

export const peeked: XApiVerb = {
  id: `${customBase}peeked`,
  display: { en: XApiVerbName.Peeked }
};

export const unpeeked: XApiVerb = {
  id: `${customBase}unpeeked`,
  display: { en: XApiVerbName.Unpeeked }
};

export const askedNext: XApiVerb = {
  id: `${customBase}next`,
  display: { en: XApiVerbName.AskedNext }
};

export const askedPrevious: XApiVerb = {
  id: `${customBase}previous`,
  display: { en: XApiVerbName.AskedPrevious }
};

export const experiencedPlayed: XApiVerb = {
  id: `${customBase}experienced-played`,
  display: { en: XApiVerbName.ExperiencedPlayed }
};

export const experiencedPaused: XApiVerb = {
  id: `${customBase}experienced-paused`,
  display: { en: XApiVerbName.ExperiencedPaused }
};

export const experiencedStopped: XApiVerb = {
  id: `${customBase}experienced-stopped`,
  display: { en: XApiVerbName.ExperiencedStopped }
};

export const screened: XApiVerb = {
  id: `${customBase}screened`,
  display: { en: XApiVerbName.Screened }
};

export const drew: XApiVerb = {
  id: `${customBase}drew`,
  display: { en: XApiVerbName.Drew }
};

export const added: XApiVerb = {
  id: `${customBase}added`,
  display: { en: XApiVerbName.Added }
};

export const removed: XApiVerb = {
  id: `${customBase}removed`,
  display: { en: XApiVerbName.Removed }
};

export const hidden: XApiVerb = {
  id: `${customBase}hidden`,
  display: { en: XApiVerbName.Hidden }
};

export const unHidden: XApiVerb = {
  id: `${customBase}un-hidden`,
  display: { en: XApiVerbName.UnHidden }
};

export const evaluated: XApiVerb = {
  id: `${customBase}evaluated`,
  display: { en: XApiVerbName.Evaluated }
};

/* -------------------- Verb Map -------------------- */

export const XApiVerbMap: Record<XApiVerbName, XApiVerb> = {
  [XApiVerbName.Initialized]: initialized,
  [XApiVerbName.Viewed]: viewed,
  [XApiVerbName.Exited]: exited,
  [XApiVerbName.Answered]: answered,
  [XApiVerbName.Completed]: completed,
  [XApiVerbName.Suspended]: suspended,
  [XApiVerbName.Resumed]: resumed,
  [XApiVerbName.Cleared]: cleared,
  [XApiVerbName.Checked]: checked,
  [XApiVerbName.ShowedAssistance]: showedAssistance,
  [XApiVerbName.ShowedAnswer]: showedAnswer,
  [XApiVerbName.ShowedDetailedFeedback]: showedDetailedFeedback,
  [XApiVerbName.Rated]: rated,
  [XApiVerbName.Shared]: shared,
  [XApiVerbName.Unshared]: unshared,
  [XApiVerbName.Peeked]: peeked,
  [XApiVerbName.Unpeeked]: unpeeked,
  [XApiVerbName.AskedNext]: askedNext,
  [XApiVerbName.AskedPrevious]: askedPrevious,
  [XApiVerbName.ExperiencedPlayed]: experiencedPlayed,
  [XApiVerbName.ExperiencedPaused]: experiencedPaused,
  [XApiVerbName.ExperiencedStopped]: experiencedStopped,
  [XApiVerbName.Screened]: screened,
  [XApiVerbName.Drew]: drew,
  [XApiVerbName.Added]: added,
  [XApiVerbName.Removed]: removed,
  [XApiVerbName.Hidden]: hidden,
  [XApiVerbName.UnHidden]: unHidden,
  [XApiVerbName.Evaluated]: evaluated
};
